import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges
} from '@angular/core';
import {FormControl, FormGroupDirective, ReactiveFormsModule} from "@angular/forms";
import {NgClass, NgForOf, NgIf} from "@angular/common";
import {BaseOption, FilterOption, SelectOption} from "../../../pages/advanced-search/types";

@Component({
  selector: 'app-filter-dropdown',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NgIf,
    NgForOf,
    NgClass
  ],
  templateUrl: './filter-dropdown.component.html',
  styleUrl: './filter-dropdown.component.scss'
})
export class FilterDropdownComponent implements OnInit, OnChanges {

  @Input() label: string = 'Select option';
  @Input() controlName: string = '';
  @Input() options: any[] = [];
  @Input() required: boolean = false;
  @Input() placeholder: string = 'Select an option';
  @Input() selectedOption: any | null = null;
  @Input() showlabel: boolean = true;
  @Input() control!: FormControl; // Make sure this is correctly typed
  @Input() disabled: boolean = false;
  @Input() customClass: string = '';

  @Output() optionSelected = new EventEmitter<any>();

  isOpen = false;
  private clickListener: () => void;

  constructor(
    public controlContainer: FormGroupDirective,
    private elRef: ElementRef,
    private renderer: Renderer2
  ) {}

  ngOnInit() {
    this.checkForSelectedValue();
  }

  ngOnChanges(changes: SimpleChanges) {
    // If options or control changes, recheck selected value
    if (
      (changes['options'] || changes['control']) &&
      this.options &&
      this.options.length
    ) {
      this.checkForSelectedValue();
    }
  }

  checkForSelectedValue() {
    if (!this.control || !this.options || this.options.length === 0) {
      return;
    }

    /*
    console.log('Checking for selected value in dropdown:', {
      control: this.control,
      value: this.control.value,
      options: this.options
    });
    */

    // If this is a first-level dropdown (filter type selection)
    // The value will be a number (filter type ID)
    if (typeof this.control.value === 'number') {
      // console.log('Looking for option with id:', this.control.value);
      this.selectedOption = this.options.find(opt => opt.id === this.control.value);
      // console.log('Found selectedOption:', this.selectedOption);
    }
      // If this is a second-level dropdown (filter value selection)
    // The value may be an array or string
    else if (this.control.value !== null && this.control.value !== undefined) {
      if (Array.isArray(this.control.value)) {
        // For select filters, value is an array of option names
        if (this.control.value.length > 0) {
          const optionName = this.control.value[0];
          this.selectedOption = this.options.find(opt =>
            opt.name === optionName ||
            (typeof opt.value === 'string' && opt.value === optionName)
          );
        }
      } else if (typeof this.control.value === 'string') {
        // For string values, match by name or value
        this.selectedOption = this.options.find(opt =>
          opt.name === this.control.value ||
          (typeof opt.value === 'string' && opt.value === this.control.value)
        );
      }

      // console.log('Found selectedOption for value:', this.selectedOption);
    }
  }

  get formControl(): FormControl {
    // Use directly provided control if available
    if (this.control) {
      return this.control;
    }

    // Otherwise try to get from form group
    if (!this.controlContainer || !this.controlName) {
      return new FormControl(); // Return dummy control to avoid errors
    }

    const group = this.controlContainer.control;
    return group?.get(this.controlName) as FormControl || new FormControl();
  }

  toggleDropdown(event: MouseEvent) {
    event.stopPropagation();

    // Don't toggle if disabled
    if (this.disabled) {
      return;
    }

    this.isOpen = !this.isOpen;

    if (this.isOpen) {
      this.addClickOutsideListener();
    } else {
      this.removeClickOutsideListener();
    }
  }

  selectOption(option: any) {
    this.selectedOption = option;

    const control = this.formControl;
    if (control) {
      // For filter type selection (first dropdown)
      if (option.type === 'select' || option.type === 'input' || option.type === 'range' || option.type === 'tags') {
        control.setValue(option.id);
      }
      // For filter value selection (second dropdown)
      else {
        // For select type filters that need array values
        const value = option.type === 'select' ? [option.name] : option.name;
        control.setValue(value);
      }

      control.markAsDirty();
    }

    this.optionSelected.emit(option);
    this.isOpen = false;
    this.removeClickOutsideListener();
  }

  private addClickOutsideListener() {
    this.clickListener = this.renderer.listen('document', 'click', (event: MouseEvent) => {
      const targetElement = event.target as HTMLElement;
      const dropdownElement = this.elRef.nativeElement;

      if (!dropdownElement.contains(targetElement)) {
        this.isOpen = false;
        this.removeClickOutsideListener();
      }
    });
  }

  private removeClickOutsideListener() {
    if (this.clickListener) {
      this.clickListener();
      this.clickListener = null;
    }
  }

  public reset(): void {
    this.selectedOption = null;
    const control = this.formControl;
    if (control) {
      control.setValue(null);
      control.markAsPristine();
      control.markAsUntouched();
    }
  }

}
